import { useEffect, useRef, useState } from 'react';
import { t, getal } from '../i18n.js';

/**
 * Pogingen per tijdvak, zoals in het prototype: gericht en scan op elkaar, één as, 2px tussen de
 * staven, en een tooltip per tijdvak. Met één reeks (`n`) voor een enkel adres.
 *
 * Getekend op de werkelijke breedte: met preserveAspectRatio="none" rekte de tekst uit (prototype).
 *
 * `reeksen` voor iets anders dan pogingen (Umbrella): `{ onder, boven, totaal, kleurBoven, label }` met de teksten voor de
 * tooltip ({n} erin) en de kleur van de bovenste reeks.
 */
const tijd = (d, metDag, alleenDag) => new Date(d).toLocaleString('nl-NL', {
  timeZone: 'Europe/Amsterdam', ...(alleenDag ? {} : { hour: '2-digit', minute: '2-digit' }), ...(metDag ? { day: 'numeric', month: 'numeric' } : {}) });

export default function Grafiek({ vakken, stap, hoogte = 200, klasse = 'bigchart', vul = false, reeksen = null }) {
  const doos = useRef(null);
  const [W, zetW] = useState(800);
  /* Met `vul` neemt de grafiek de hoogte van zijn vak (SOC-scherm); anders een vaste hoogte. */
  const [Hvak, zetHvak] = useState(hoogte);
  const [tip, zetTip] = useState(null);
  useEffect(() => {
    const el = doos.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => { zetW(Math.max(300, el.clientWidth)); if (vul) zetHvak(Math.max(90, el.clientHeight)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [vul]);

  const H = vul ? Hvak : hoogte, padL = 44, padB = 22, padT = 14;
  const rij = vakken.map((v) => ({ ...v, g: v.gericht ?? 0, sc: v.scan ?? v.n ?? 0 }));
  const hoogst = Math.max(1, ...rij.map((v) => v.g + v.sc));
  const macht = 10 ** Math.floor(Math.log10(hoogst));
  /* Minstens 2, zodat de as hele aantallen toont en geen "0,5 pogingen". */
  const top = Math.max(2, Math.ceil(hoogst / macht) * macht);
  const ih = H - padT - padB;
  const bw = (W - padL) / rij.length;
  const y = (v) => padT + ih - (v / top) * ih;
  const metDag = stap >= 6 * 3600;
  /* Per dag: alleen de datum, geen 00:00. */
  const alleenDag = stap >= 86400;

  return (
    <div className={klasse} ref={doos}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={reeksen?.label ?? t('Pogingen per tijdvak')}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={W} y1={y(top * f)} y2={y(top * f)} stroke="var(--line)" strokeWidth="1" />
            <text x={padL - 6} y={y(top * f) + 4} textAnchor="end" className="cl">{getal(top * f)}</text>
          </g>
        ))}
        {rij.map((v, i) => {
          const x = padL + i * bw + 1;
          const w = Math.max(1, bw - 2);
          return (
            <g key={i}>
              {v.sc > 0 && <rect x={x} y={y(v.sc)} width={w} height={y(0) - y(v.sc)} fill="var(--bar, var(--cyan-ink))" rx="2" />}
              {v.g > 0 && <rect x={x} y={y(v.sc) - (y(0) - y(v.g)) - (v.sc ? 1 : 0)} width={w} height={y(0) - y(v.g)} fill={reeksen?.kleurBoven ?? 'var(--st-serious)'} rx="2" />}
              {i % Math.ceil(rij.length / 6) === 0 && <text x={x + w / 2} y={H - 5} textAnchor="middle" className="cl">{tijd(v.from, metDag, alleenDag)}</text>}
              <rect x={padL + i * bw} y={padT} width={bw} height={ih} fill="transparent"
                onMouseEnter={() => zetTip({ v, x: padL + i * bw + bw / 2 })} onMouseLeave={() => zetTip(null)} />
            </g>
          );
        })}
      </svg>
      {tip && (
        <div className="ctip" style={{ left: tip.x / W * 100 + '%' }}>
          <b>{tijd(tip.v.from, metDag, alleenDag)}</b><br />{reeksen ? reeksen.totaal(getal(tip.v.g + tip.v.sc)) : t('{n} pogingen', { n: getal(tip.v.g + tip.v.sc) })}
          {reeksen && <>
            {tip.v.g > 0 && <><br />{reeksen.boven(getal(tip.v.g))}</>}
            {tip.v.sc > 0 && <><br />{reeksen.onder(getal(tip.v.sc))}</>}
          </>}
          {!reeksen && vakken[0]?.gericht !== undefined && <>
            {tip.v.g > 0 && <><br />{t('met open alert: {n}', { n: getal(tip.v.g) })}</>}
            {tip.v.sc > 0 && <><br />{t('zonder alert: {n}', { n: getal(tip.v.sc) })}</>}
          </>}
        </div>
      )}
    </div>
  );
}
